"use client";

import { Flame } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Thesis() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: tenetsRef,
    isVisible: tenetsVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const headlineWords = [{
    text: "We",
    italic: false
  }, {
    text: "believe",
    italic: false
  }, {
    text: "that",
    italic: false
  }, {
    text: "most",
    italic: false
  }, {
    text: "lost",
    italic: false
  }, {
    text: "marks",
    italic: false,
    breakAfter: true
  }, {
    text: "are",
    italic: true
  }, {
    text: "not",
    italic: true
  }, {
    text: "knowledge",
    italic: true
  }, {
    text: "gaps,",
    italic: true
  }, {
    text: "but",
    italic: true
  }, {
    text: "process",
    italic: true
  }, {
    text: "failures.",
    italic: true
  }];
  const tenets = [{
    numeral: "01.",
    text: "Assessment is not a final judgment, but a diagnostic map. We look past the result to the mechanical failure of the student's process."
  }, {
    numeral: "02.",
    text: "Getting the specifics right is our primary offering. By mapping answers to specific mark-scheme logic, we surface the exact point where the method broke down."
  }, {
    numeral: "03.",
    text: "Every error is a data point for growth. In identifying the 'why' behind the lost mark, we help students master the method, not just memorize the answer."
  }];
  return <section data-section-id="996" id="the-core-thesis" className="bg-background text-foreground relative overflow-hidden min-h-screen py-24 md:py-32 px-6 border-t border-b border-border/40">
      {/* Corner accent marks */}
      <div className="absolute top-6 left-6 size-6 border-t border-l border-[hsl(39_100%_80%/0.55)] pointer-events-none" aria-hidden="true" />
      <div className="absolute top-6 right-6 size-6 border-t border-r border-[hsl(39_100%_80%/0.55)] pointer-events-none" aria-hidden="true" />
      <div className="absolute bottom-6 left-6 size-6 border-b border-l border-[hsl(39_100%_80%/0.55)] pointer-events-none" aria-hidden="true" />
      <div className="absolute bottom-6 right-6 size-6 border-b border-r border-[hsl(39_100%_80%/0.55)] pointer-events-none" aria-hidden="true" />

      <div className="pointer-events-none absolute inset-0 opacity-[0.04] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")"
    }} />

      <div className="relative max-w-4xl mx-auto flex flex-col items-center text-center">
        <div ref={eyebrowRef} className={`flex flex-col items-center transition-all duration-1000 ease-out ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="mb-6" style={{
          color: "hsl(39 100% 80%)",
          animation: "flameFlicker 3s ease-in-out infinite"
        }}>
            <Flame className="w-7 h-7" strokeWidth={1.25} fill="currentColor" fillOpacity={0.2} />
          </div>

          <div className="font-default text-sm md:text-base tracking-[0.35em] uppercase mb-10 md:mb-14" style={{
          color: "hsl(39 100% 80%)"
        }}>
            <span>&middot;&nbsp;&nbsp;The Core Thesis&nbsp;&nbsp;&middot;</span>
          </div>
        </div>

        <div className="relative w-full flex justify-center">
          {/* Soft radial gold glow */}
          <div className="pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[140%] rounded-lg blur-3xl" style={{
          background: "radial-gradient(ellipse at center, hsl(39 100% 80% / 0.12) 0%, transparent 70%)"
        }} aria-hidden="true" />

          <h2 ref={headlineRef} className="font-default font-light text-foreground leading-[1.05] tracking-tight relative z-10" style={{
          fontSize: "clamp(2.75rem, 6vw, 5.5rem)"
        }}>
            {headlineWords.map((word, i) => <span key={i} data-index={i} className="inline-block transition-all ease-out" style={{
            opacity: headlineVisible ? 1 : 0,
            transform: headlineVisible ? "translateY(0)" : "translateY(20px)",
            transitionDuration: "900ms",
            transitionDelay: `${i * 110}ms`,
            fontStyle: word.italic ? "italic" : "normal",
            fontWeight: word.italic ? 300 : 400
          }}>
                {word.text}
                {word.breakAfter ? <>
                    <br />
                  </> : <>&nbsp;</>}
              </span>)}
          </h2>
        </div>

        <div className={`mt-12 md:mt-16 mb-12 md:mb-16 transition-all duration-1000 delay-700 ease-out ${headlineVisible ? "opacity-100 scale-x-100" : "opacity-0 scale-x-0"}`} style={{
        width: "60px",
        height: "1px",
        backgroundColor: "hsl(39 100% 80%)"
      }} />

        <div ref={tenetsRef} className="flex flex-col items-center w-full max-w-3xl">
          {tenets.map((tenet, i) => <div key={i} data-index={i} className="flex flex-col items-center w-full transition-all duration-1000 ease-out" style={{
          opacity: tenetsVisible ? 1 : 0,
          transform: tenetsVisible ? "translateY(0)" : "translateY(24px)",
          transitionDelay: `${i * 250}ms`
        }}>
              <div className="flex flex-col items-center w-full border border-border/30 rounded-lg px-8 py-10 md:px-12 md:py-12 hover:border-[hsl(39_100%_80%/0.4)] hover:bg-white/[0.02] transition-colors duration-300">
                <div className="font-default italic text-2xl md:text-3xl mb-6" style={{
              color: "hsl(39 100% 80%)"
            }}>
                  {tenet.numeral}
                </div>

                <p className="font-default text-foreground/85 leading-relaxed max-w-2xl px-4" style={{
              fontSize: "clamp(1.125rem, 1.6vw, 1.3125rem)"
            }}>
                  {tenet.text}
                </p>
              </div>

              {i < tenets.length - 1 && <div className="flex items-center justify-center my-10 md:my-12" aria-hidden="true" style={{
            color: "hsl(39 100% 80% / 0.6)"
          }}>
                  <svg width="36" height="14" viewBox="0 0 36 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 7 Q 9 1, 18 7 T 34 7" stroke="currentColor" strokeWidth="1" strokeLinecap="round" fill="none" />
                    <circle cx="18" cy="7" r="1.25" fill="currentColor" />
                  </svg>
                </div>}
            </div>)}
        </div>

        <div className={`mt-20 md:mt-24 transition-all duration-1000 delay-500 ease-out ${tenetsVisible ? "opacity-100" : "opacity-0"}`} style={{
        color: "hsl(39 100% 80% / 0.5)"
      }} aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="7" cy="7" r="1.5" fill="currentColor" />
          </svg>
        </div>
      </div>

      <style>{`
        @keyframes flameFlicker {
          0%, 100% { opacity: 1; transform: scale(1); }
          25% { opacity: 0.85; transform: scale(0.97); }
          50% { opacity: 1; transform: scale(1.02); }
          75% { opacity: 0.9; transform: scale(0.99); }
        }
      `}</style>
    </section>;
}